import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * Phase 3.5A / 3.5D — Account-link confirmation landing page.
 *
 * The backend emails a one-time link to the existing collector's email
 * whenever a Google/Apple sign-in matches an existing password account.
 * The email button targets `{PUBLIC_APP_URL}/link-confirm?token={raw}`.
 * This component POSTs that token to
 * `/api/auth/oauth/link/confirm`, which atomically attaches the identity
 * (or returns 400/409 if invalid/expired/already-linked).
 *
 * On success we redirect the collector to the safe `return_to` path the
 * backend threaded through the token row (falls back to `/dashboard`).
 */
export default function LinkConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "loading", provider: null, returnTo: "/dashboard", error: "" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", provider: null, returnTo: "/dashboard",
        error: "This confirmation link is missing its token." });
      return;
    }
    const body = new URLSearchParams();
    body.append("token", token);
    api.post("/auth/oauth/link/confirm", body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      .then((r) => {
        const data = r.data || {};
        setState({
          status: "success",
          provider: data.provider || null,
          returnTo: data.return_to || "/dashboard",
          error: "",
        });
        toast.success(
          data.provider
            ? `${data.provider[0].toUpperCase()}${data.provider.slice(1)} connected to your account.`
            : "Account linked.",
        );
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        setState({
          status: "error", provider: null, returnTo: "/dashboard",
          error: formatApiError(detail) || err.message || "Could not confirm the link.",
        });
      });
  }, [token]);

  const providerLabel = state.provider
    ? state.provider[0].toUpperCase() + state.provider.slice(1)
    : "provider";

  return (
    <div className="min-h-[calc(100vh-140px)] grid place-items-center px-5 py-14"
         data-testid="link-confirm-page">
      <SEO title="Confirm account link · Collector Registry" />
      <div className="w-full max-w-md">
        <div className="kicker kicker-red mb-6">Gil Maakaroun · Collector Registry</div>

        {state.status === "loading" && (
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-8 shadow-[6px_6px_0_var(--ink)]"
               data-testid="link-confirm-loading">
            <div className="flex items-center gap-3">
              <Loader2 className="animate-spin" size={22} />
              <h1 className="font-display text-2xl">Confirming your link…</h1>
            </div>
            <p className="mt-3 text-sm text-[var(--smoke)]">
              We&apos;re attaching the provider identity to your account. This only
              takes a moment.
            </p>
          </div>
        )}

        {state.status === "success" && (
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-8 shadow-[6px_6px_0_var(--panini-red)]"
               data-testid="link-confirm-success">
            <div className="flex items-center gap-3 text-[var(--panini-red)]">
              <CheckCircle2 size={26} />
              <h1 className="font-display text-2xl text-[var(--ink)]">
                {providerLabel} connected.
              </h1>
            </div>
            <p className="mt-3 text-sm text-[var(--smoke)]">
              You can now sign in with {providerLabel} in addition to your
              password. No changes were made to your collector ID, certificates,
              or claims.
            </p>
            <button
              type="button"
              onClick={() => navigate(state.returnTo || "/dashboard", { replace: true })}
              data-testid="link-confirm-continue"
              className="btn-red mt-6 w-full justify-center text-sm"
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {state.status === "error" && (
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-8 shadow-[6px_6px_0_var(--ink)]"
               data-testid="link-confirm-error">
            <div className="flex items-center gap-3 text-[var(--ink)]">
              <XCircle size={26} />
              <h1 className="font-display text-2xl">We couldn&apos;t confirm this link.</h1>
            </div>
            <p className="mt-3 text-sm text-[var(--smoke)]" data-testid="link-confirm-error-detail">
              {state.error}
            </p>
            <p className="mt-3 text-sm text-[var(--smoke)]">
              The link may have expired, already been used, or the provider
              identity may have been claimed by another account in the meantime.
              You can sign in with your password and try connecting the provider
              again from your Security tab.
            </p>
            <Link
              to="/login"
              data-testid="link-confirm-back-to-login"
              className="btn-red mt-6 w-full justify-center text-sm"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
